import { Box } from '@radix-ui/themes';
import { HardwareDiagnosticReminder } from 'components/main/sidebar/reminder/hardware-diagnostic';
import { InstallReminder } from 'components/main/sidebar/reminder/installation';
import { MachineContext } from 'contexts/machine.context';
import { InstallStep } from 'lib_ts/enums/installation';
import { useContext, useMemo } from 'react';
import { useHardwareDiagnosticStore } from 'components/machine/dialogs/hardware-diagnostic/store/use-hardware-diagnostic-store';
import { useAuthStore } from 'stores/use-auth-store';

enum ReminderType {
  None = 'None',
  Installer = 'Installer',
  HardwareDiagnostics = 'HardwareDiagnostics',
}

export const SidebarReminder = (props: { expanded: boolean }) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { isActive, machine } = useContext(MachineContext);
  const inProgressHwd = useHardwareDiagnosticStore((s) => s.inProgressHwd);

  const { last_installation: install } = machine;

  const remindInstaller = useMemo(() => {
    if (!isActive) {
      // hide while d/c from machine
      return false;
    }

    if (!install) {
      // no installation exists
      return false;
    }

    if (install.end_date) {
      // (previous) installation is already complete
      return false;
    }

    if (install.standalone === InstallStep.BallType) {
      // standalone BTC is shown to everyone
      return true;
    }

    if (userRoleFeatureFlags.adminMachines.install) {
      // full install and/or standalone RT is only shown to some users
      return true;
    }

    // default is not to show anything
    return false;
  }, [userRoleFeatureFlags, isActive, install]);

  const remindHwd = useMemo(() => {
    if (!isActive) {
      // hide while d/c from machine
      return false;
    }

    if (!inProgressHwd) {
      // no HWD found
      return false;
    }

    return true;
  }, [isActive, inProgressHwd]);

  // only 1 reminder should be shown at a time
  const visibleReminder = remindInstaller
    ? ReminderType.Installer
    : remindHwd
      ? ReminderType.HardwareDiagnostics
      : ReminderType.None;

  switch (visibleReminder) {
    case ReminderType.Installer: {
      if (!machine.last_installation) {
        return null;
      }

      return (
        <Box mb="3">
          <InstallReminder
            install={machine.last_installation}
            expanded={props.expanded}
          />
        </Box>
      );
    }

    case ReminderType.HardwareDiagnostics: {
      if (!inProgressHwd) {
        return null;
      }

      return (
        <Box mb="3">
          <HardwareDiagnosticReminder
            expanded={props.expanded}
            hwd={inProgressHwd}
          />
        </Box>
      );
    }

    default: {
      return null;
    }
  }
};
